import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { publicAPI } from '../lib/api';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';
import { PageHero } from './ModernPage';
import { TestimonialCard } from './ModernTestimonials';

// ─────────────────────────────────────────────────────────────────────────
// «Moderno» list pages (s141) — /featured-projects and /testimonials on the
// internal-page standard (D-2026-131): PageHero + 1440 shell. Same data as
// the home sections (Portfolio / Testimonials managers); the header reads the
// section's CMS config (aurex_portfolio_cfg / aurex_testimonials_cfg).
// Rendered only when settings.active_theme === 'modern'.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const GRAPHITE = '#16171a';
const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';
const img = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);
const plain = (h) => String(h || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
const num = (i) => String(i + 1).padStart(2, '0');

function useCfg(key) {
  const [cfg, setCfg] = React.useState({});
  React.useEffect(() => {
    fetch(`${API}/api/public/aurex/${key}`).then(r => r.json()).then(d => setCfg((d && d.config) || {})).catch(() => {});
  }, [key]);
  return cfg;
}

function Cover({ src }) {
  const [broken, setBroken] = React.useState(false);
  const url = img(src);
  if (!url || broken) return <span className="block w-full h-full" style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--color-accent, #ff9c1b) 35%, #fff), var(--color-accent, #ff9c1b))' }} />;
  return <img src={url} alt="" loading="lazy" onError={() => setBroken(true)} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />;
}

function Empty({ children }) {
  return <p className="py-16 text-center text-base" style={{ color: 'var(--color-body-text, #475569)' }}>{children}</p>;
}

/* ── /featured-projects ───────────────────────────────────────────────── */
export function ModernFeaturedProjectsPage() {
  const tt = useT();
  const { lang } = useLang();
  const cfg = useCfg('aurex_portfolio_cfg');
  const [items, setItems] = React.useState(null);
  React.useEffect(() => { publicAPI.getPortfolio().then(r => setItems(r.data || [])).catch(() => setItems([])); }, []);
  const list = (items || []).filter(p => p.visible !== false && itemHasLocale(p.title, lang));
  return (
    <div data-testid="featured-projects-page" className="bg-white">
      <PageHero title={tt(cfg.title) || 'Featured Projects'} summary={tt(cfg.subtitle)} banner={list[0] && list[0].image} />
      <div className={`${shell} py-14 lg:py-20`}>
        {items && !list.length && <Empty>{lang === 'es' ? 'Aún no hay proyectos.' : 'No projects yet.'}</Empty>}
        <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p, i) => {
            const meta = [num(i), tt(p.category)].filter(Boolean).join(' · ');
            const body = (
              <>
                <span className="block aspect-[4/3] overflow-hidden rounded-[3px] mb-2" style={{ background: GRAPHITE }}><Cover src={p.image} /></span>
                <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: 'var(--accent-strong, #b86500)' }}>{meta}</span>
                <span className="font-semibold inline-flex items-start justify-between gap-3 transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: '1.3rem', lineHeight: 1.25, color: 'var(--color-heading, #1a2332)' }}>
                  {tt(p.title)}{p.link && <ArrowUpRight className="w-4 h-4 flex-none mt-1.5" style={{ color: 'var(--accent-strong, #b86500)' }} />}
                </span>
                {plain(tt(p.description)) && <span className="text-[15px] leading-relaxed line-clamp-3" style={{ color: 'var(--color-body-text, #475569)' }}>{plain(tt(p.description))}</span>}
              </>
            );
            return p.link
              ? <a key={p.id} href={p.link} target={p.open_in_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="group flex flex-col gap-3" data-testid={`project-card-${p.id}`}>{body}</a>
              : <div key={p.id} className="group flex flex-col gap-3" data-testid={`project-card-${p.id}`}>{body}</div>;
          })}
        </div>
      </div>
    </div>
  );
}

/* ── /testimonials ─────────────────────────────────────────────────────── */
export function ModernTestimonialsPage() {
  const tt = useT();
  const { lang } = useLang();
  const cfg = useCfg('aurex_testimonials_cfg');
  const [items, setItems] = React.useState(null);
  React.useEffect(() => { publicAPI.getTestimonials().then(r => setItems(r.data || [])).catch(() => setItems([])); }, []);
  const list = (items || []).filter(t => t.visible !== false && itemHasLocale(t.content, lang));
  return (
    <div data-testid="testimonials-page" style={{ background: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 5%, #f6f5f2)' }}>
      <PageHero title={tt(cfg.title) || 'What People Say'} summary={tt(cfg.subtitle)} />
      <div className={`${shell} py-14 lg:py-20`}>
        {items && !list.length && <Empty>{lang === 'es' ? 'Aún no hay testimonios.' : 'No testimonials yet.'}</Empty>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 items-start">
          {list.map(t => <TestimonialCard key={t.id} t={t} />)}
        </div>
      </div>
    </div>
  );
}
